/**
 * Drill-down: "mesmos registros, mesmo histórico" (D-03) na prática.
 *
 * As telas de lista NÃO recalculam o filtro do KPI. Elas chamam
 * `selectKpiRecords` (o mesmo seletor que montou o número no dashboard) e
 * exibem exatamente aqueles ids. Assim é impossível o card dizer "7 pedidos
 * pendentes" e a lista mostrar 6: o conjunto é o mesmo objeto de verdade.
 *
 * Puro e sem I/O — testado em `tests/plataforma-dashboard.test.ts`.
 */
import { selectKpiRecords, type DashboardDataset, type KpiId } from "./kpis.js";
import type {
  PlatformCatalogEntry,
  PlatformConversation,
  PlatformNotification,
  PlatformOrder,
  PlatformRmaCase,
  PricingMarginRow
} from "../api/types.js";

const KPI_IDS: readonly KpiId[] = [
  "faturamento",
  "pedidos",
  "margem_estimada",
  "pedidos_pendentes",
  "falhas_sincronizacao",
  "estoque_critico",
  "conversas_aguardando",
  "garantias_abertas",
  "sla_atendimento"
];

/** Lê o parâmetro `?kpi=` da rota; valor desconhecido vira `null` (lista completa). */
export function parseKpiId(raw: string | null | undefined): KpiId | null {
  if (!raw) return null;
  return (KPI_IDS as readonly string[]).includes(raw) ? (raw as KpiId) : null;
}

function restrict<T>(items: T[], ids: string[] | null, idOf: (item: T) => string): T[] {
  if (ids === null) return items;
  const wanted = new Set(ids);
  return items.filter((item) => wanted.has(idOf(item)));
}

function idsFor(kpiId: KpiId | null, dataset: DashboardDataset, now: Date): string[] | null {
  if (!kpiId) return null;
  return selectKpiRecords(kpiId, dataset, now).ids;
}

export function ordersForKpi(dataset: DashboardDataset, kpiId: KpiId | null, now: Date = new Date()): PlatformOrder[] {
  return restrict(dataset.orders, idsFor(kpiId, dataset, now), (order) => order.id);
}

export function conversationsForKpi(
  dataset: DashboardDataset,
  kpiId: KpiId | null,
  now: Date = new Date()
): PlatformConversation[] {
  return restrict(dataset.conversations, idsFor(kpiId, dataset, now), (conversation) => conversation.id);
}

export function marginRowsForKpi(dataset: DashboardDataset, kpiId: KpiId | null, now: Date = new Date()): PricingMarginRow[] {
  return restrict(dataset.pricing.rows, idsFor(kpiId, dataset, now), (row) => row.productId);
}

export function catalogEntriesForKpi(
  dataset: DashboardDataset,
  kpiId: KpiId | null,
  now: Date = new Date()
): PlatformCatalogEntry[] {
  return restrict(dataset.catalog, idsFor(kpiId, dataset, now), (entry) => entry.product.id);
}

export function notificationsForKpi(
  dataset: DashboardDataset,
  kpiId: KpiId | null,
  now: Date = new Date()
): PlatformNotification[] {
  return restrict(dataset.notifications, idsFor(kpiId, dataset, now), (notification) => notification.id);
}

/** `null` = fonte de garantias (Task 15) ainda não publicada. */
export function rmaCasesForKpi(
  dataset: DashboardDataset,
  kpiId: KpiId | null,
  now: Date = new Date()
): PlatformRmaCase[] | null {
  if (dataset.rmaCases === null) return null;
  return restrict(dataset.rmaCases, idsFor(kpiId, dataset, now), (rmaCase) => rmaCase.id);
}
